import { useState } from 'react';
import type { UserFormData } from '../../types/signup';
import { ProgressIndicator } from './ProgressIndicator';
import { PasswordField } from '../PasswordField';

interface UserCreationProps {
  onSubmit: (data: UserFormData) => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}

export const UserCreation = ({ onSubmit, onBack, loading, error }: UserCreationProps) => {
  const [formData, setFormData] = useState<UserFormData>({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
  });
  const handleChange = (field: keyof UserFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isFormValid = 
    formData.first_name.trim() &&
    formData.last_name.trim() &&
    formData.email.trim() &&
    formData.password.length >= 6;

  return (
    <>
      <div className="flex-1 overflow-y-auto no-scrollbar p-8 md:p-16 flex flex-col justify-center">
        <ProgressIndicator currentStep={2} />

        {/* Back Button */}
        <button
          onClick={onBack}
          disabled={loading}
          className="flex items-center gap-2 text-[#101822]/60 hover:text-primary transition-colors mb-6 disabled:opacity-50"
        >
          <span className="material-icons-round text-xl">arrow_back</span>
          <span className="text-sm font-semibold">Volver a los detalles de la clínica</span>
        </button>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Header */}
          <div className="space-y-3">
            <h1 className="text-3xl md:text-4xl font-extrabold text-[#101822] tracking-tight leading-tight text-balance">
              Cuéntanos sobre ti
            </h1>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#101822] text-sm font-semibold ml-1" htmlFor="firstName">
                Nombre
              </label>
              <input
                className="w-full bg-white border-2 border-[#101822]/5 rounded-xl px-4 py-3 text-base text-[#101822] placeholder-[#101822]/20 focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all outline-none"
                id="firstName"
                placeholder="Juan"
                type="text"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                required
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <label className="text-[#101822] text-sm font-semibold ml-1" htmlFor="lastName">
                Apellido
              </label>
              <input
                className="w-full bg-white border-2 border-[#101822]/5 rounded-xl px-4 py-3 text-base text-[#101822] placeholder-[#101822]/20 focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all outline-none"
                id="lastName"
                placeholder="García"
                type="text"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Email Field */}
          <div className="flex flex-col gap-2">
            <label className="text-[#101822] text-sm font-semibold ml-1" htmlFor="email">
              Correo electrónico
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#101822]/40">
                <span className="material-symbols-outlined text-xl">mail</span>
              </div>
              <input
                className="w-full bg-white border-2 border-[#101822]/5 rounded-xl pl-12 pr-4 py-3 text-base text-[#101822] placeholder-[#101822]/20 focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all outline-none"
                id="email"
                placeholder="juan.garcia@ejemplo.com"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Password Field */}
          <PasswordField
            id="password"
            label="Contraseña"
            value={formData.password}
            onChange={(value) => handleChange('password', value)}
            placeholder="Mínimo 6 caracteres"
            required
            minLength={6}
            hint="La contraseña debe tener al menos 6 caracteres"
            variant="signup"
          />

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
        </form>
      </div>

      {/* Bottom Action Buttons */}
      <div className="p-8 md:p-12 border-t border-[#101822]/5 bg-white/60 backdrop-blur-xl">
        <button
          onClick={handleSubmit}
          disabled={loading || !isFormValid}
          className="w-full group relative overflow-hidden rounded-2xl !bg-primary !p-5 shadow-xl shadow-primary/20 transition-all duration-300 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          <div className="relative flex items-center justify-center gap-3">
            <span className="font-bold text-white text-lg tracking-wide">
              {loading ? 'Creando cuenta...' : 'Crear cuenta'}
            </span>
            {!loading && (
              <span className="material-icons-round text-white transition-transform group-hover:translate-x-1">
                check_circle
              </span>
            )}
          </div>
        </button>
      </div>
    </>
  );
};
